import {
  DeviceType,
  WeightUnit,
  ChartDataPoint,
  MedisanteDevice,
  MedisanteMetric,
  SensorsSortBy,
} from './types';
import { MedisanteTargetSystem } from '../TargetSystems/types';
import { onlyUnique } from '../../utils/helpers';
import {
  Direction,
  sort,
  SortOption,
  SortOptionSelectItem,
  SortState,
} from '../../utils/sort';

export const CHART_COLORS = ['rgb(102, 110, 117)', 'rgb(70, 212, 230)'];

export const getLabelForDeviceType = (deviceType: DeviceType) => {
  switch (deviceType) {
    case DeviceType.Glucose:
      return 'Glucose';
    case DeviceType.BloodPressure:
      return 'Blood Pressure';
    case DeviceType.Pulse:
      return 'Pulse';
    case DeviceType.BodyComposition:
      return 'Impedance';
    case DeviceType.BodyWeight:
      return 'Weight';
    case DeviceType.ECG:
      return 'ECG';
    case DeviceType.Temperature:
      return 'Temperature';
    case DeviceType.SpO2:
      return 'SpO2';
    case DeviceType.Gateway:
      return 'Gateway';
    default:
      return deviceType;
  }
};

export const getDeviceTypePriority = (deviceType: DeviceType) => {
  switch (deviceType) {
    case DeviceType.BodyWeight:
      return 1;
    case DeviceType.BodyComposition:
      return 2;
    case DeviceType.Glucose:
      return 3;
    case DeviceType.BloodPressure:
      return 4;
    case DeviceType.Pulse:
      return 5;
    case DeviceType.ECG:
      return 6;
    case DeviceType.Temperature:
      return 7;
    case DeviceType.SpO2:
      return 8;
    case DeviceType.Gateway:
      return 9;
  }
};

export const getWeightUnitName = (weightUnit: WeightUnit) => {
  switch (weightUnit) {
    case WeightUnit.Pound:
      return 'Pound';
    case WeightUnit.Stone:
      return 'Stone';
    default:
      return 'Kilogram';
  }
};

export const getSortedDeviceTypes = (device: MedisanteDevice | null) => {
  const types =
    device && device.properties && device.properties.types
      ? device.properties.types.map((type) => ({
          type,
          priority: getDeviceTypePriority(type),
        }))
      : [];

  return sort(types, { priority: Direction.ASC }).map((t) => t.type);
};

export const getEmptyTargetSystem = (id: string) => ({
  id,
  name: '',
  url: '',
});

export const getTargetSystemFromID = (
  id: string,
  targetSystems: MedisanteTargetSystem[]
) => targetSystems.find((ts) => ts.id === id) || getEmptyTargetSystem(id);

export const hasBatteryVoltageData = (data: ChartDataPoint[]) =>
  data.some((d) => typeof d.batteryVoltage === 'number');

export const hasSignalStrengthData = (data: ChartDataPoint[]) => {
  return data.some((d) => {
    const key = Object.keys(d).find((k) => k.startsWith('signalStrength'));
    return !!key && typeof d[key] === 'number';
  });
};

export const getSignalTechnologies = (metrics: MedisanteMetric[]) => {
  const signalTechnologies = metrics
    .filter((m) => m.data)
    .map((m) => m.data!.signalTechnology)
    .filter(onlyUnique());

  return signalTechnologies.sort().reverse();
};

export const hasActiveToken = (device: MedisanteDevice) => {
  if (!device) {
    return false;
  }
  return (
    device.token !== undefined &&
    device.token.confirmed !== undefined &&
    device.token.confirmed.value.length > 0
  );
};

export const getLastMeasurementAtForDevice = (
  device: MedisanteDevice,
  isDemo: boolean
) => {
  if (isDemo) {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  return device.lastMeasurementAt ? new Date(device.lastMeasurementAt) : null;
};

export const sortSensors = (
  devices: MedisanteDevice[],
  sortState: SortState<MedisanteDevice>,
  totalView: boolean
) => {
  return sort(devices, sortState, {
    serialNumber: (d) => d.serialNumber,
    model: (d) => d.model,
    mac: (d) => d.mac || '',
    measurementCount: (d) =>
      (totalView ? d.measurementCount : d.measurementTripCount) || 0,
  });
};

export const sensorsSortingReducer = (
  state: SortState<MedisanteDevice>,
  action: SortOption<SensorsSortBy>
): SortState<MedisanteDevice> => {
  const getDirectionByPrevious = (direction?: Direction) => {
    return direction === Direction.ASC ? Direction.DESC : Direction.ASC;
  };

  switch (action.sortBy) {
    case SensorsSortBy.SERIAL_NUMBER:
      return {
        serialNumber:
          action.direction || getDirectionByPrevious(state.serialNumber),
      };
    case SensorsSortBy.MAC:
      return {
        mac: action.direction || getDirectionByPrevious(state.mac),
      };
    case SensorsSortBy.MODEL:
      return {
        model: action.direction || getDirectionByPrevious(state.model),
      };
    case SensorsSortBy.MEASUREMENTS:
      return {
        measurementCount:
          action.direction || getDirectionByPrevious(state.measurementCount),
      };
    default:
      return state;
  }
};

export const mobileSortOptions: SortOptionSelectItem<SensorsSortBy>[] = [
  {
    value: { sortBy: SensorsSortBy.SERIAL_NUMBER, direction: Direction.ASC },
    label: 'Serial Number (ascending)',
    id: 'serial-number-asc',
  },
  {
    value: { sortBy: SensorsSortBy.SERIAL_NUMBER, direction: Direction.DESC },
    label: 'Serial Number (descending)',
    id: 'serial-number-desc',
  },
  {
    value: { sortBy: SensorsSortBy.MAC, direction: Direction.ASC },
    label: 'Mac (ascending)',
    id: 'imei-asc',
  },
  {
    value: { sortBy: SensorsSortBy.MAC, direction: Direction.DESC },
    label: 'Mac (descending)',
    id: 'imei-desc',
  },
  {
    value: {
      sortBy: SensorsSortBy.MODEL,
      direction: Direction.ASC,
    },
    label: 'Model (ascending)',
    id: 'model-asc',
  },
  {
    value: {
      sortBy: SensorsSortBy.MODEL,
      direction: Direction.DESC,
    },
    label: 'Model (descending)',
    id: 'model-desc',
  },
  {
    value: {
      sortBy: SensorsSortBy.MEASUREMENTS,
      direction: Direction.ASC,
    },
    label: 'Measurements (ascending)',
    id: 'measurements-asc',
  },
  {
    value: {
      sortBy: SensorsSortBy.MEASUREMENTS,
      direction: Direction.DESC,
    },
    label: 'Measurements (descending)',
    id: 'measurements-desc',
  },
];
